import { AdvancedSearchInfoLink } from 'components/Blotters/advanced-search-info-link';
import { ActivityFilter } from 'components/Blotters/Filter/ActivityFilter';
import { AdvancedDateFilter } from 'components/Blotters/Filter/AdvancedDateFilter';
import { AdvancedFilterButton } from 'components/Blotters/Filter/AdvancedFilterButton';
import { AdvancedFilters } from 'components/Blotters/Filter/AdvancedFilters';
import { BlotterTitleContainer, BlotterTitleLabel } from 'components/Blotters/Filter/blotter-title';
import { DateFilter } from 'components/Blotters/Filter/DateFilter';
import { ExportCsv } from 'components/Blotters/Filter/ExportCsv';
import { FullTextSearch } from 'components/Blotters/Filter/full-text-search';
import { ProductFilterComponent } from 'components/Blotters/Quotes/Filter/ProductFilter';
import { useBlotterItemsFetch, useDlpBlotterItemsFetch } from 'components/Blotters/Trades/Filter/hooks';
import { useAppSelector } from 'hooks/useAppSelector';
import type { ComponentType, FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getTradesBlotterAvailablePlatforms,
  getTradesBlottersAvailablePlatformsFetchingStatus,
} from 'state/blotters/trades/tradesBlotters.selector';
import { TradesOrProducts } from 'state/blotters/trades/types/blotterItem';

import { GenericErrorBoundary } from '../../../common-form/builder/form/GenericErrorBoundary';
import { BlotterErrorIcon } from '../../BlotterErrorIcon';
import { BlotterSpinner } from '../../BlotterSpinner';
import { PlatformFilterSelector } from '../../Filter/Platform/PlatformFilter';
import { BlotterTypeFilter } from './BlotterTypeFilter';
import {
  connectFilterComponent,
  FilterOwnProps,
  TradesBlottersFilterProps,
  TradesOrProductsFilterProps,
} from './Filter.connect';

function isProductsOrTradesProps(val: TradesBlottersFilterProps): val is TradesOrProductsFilterProps {
  return val.blotterTypeFilter === 'trades' || val.blotterTypeFilter === 'products';
}

export const TradesBlotterFilter: FC<TradesBlottersFilterProps> = props => {
  const { formatMessage } = useIntl();
  const fetchingPlatformsStatus = useAppSelector(getTradesBlottersAvailablePlatformsFetchingStatus);
  const platforms = useAppSelector(getTradesBlotterAvailablePlatforms);

  const fetch = useBlotterItemsFetch(props.lastBlotterRequest, props.blotterTypeFilter as TradesOrProducts);
  const dlpFetch = useDlpBlotterItemsFetch({
    requestParams: props.requestParams,
  });
  return (
    <GenericErrorBoundary>
      <div className="trades-blotter-filters w-100 align-items-center border-top" data-e2e="trades-blotter-filters">
        <div className="w-100 d-flex flex-row flex-wrap flex-md-nowrap">
          <BlotterTitleContainer>
            <BlotterTitleLabel>
              <FormattedMessage id={`blotter.filter.blotterType.${props.blotterTypeFilter}`} />
            </BlotterTitleLabel>
            {props.error && <BlotterErrorIcon message={formatMessage({ id: 'blotter.filter.label.error' })} />}
            <BlotterSpinner className="flex-shrink-0 mx-2 my-auto" hide={!props.loading} />
          </BlotterTitleContainer>
          <BlotterTypeFilter value={props.blotterTypeFilter} onSelect={props.onBlotterTypeFilterChanged} />
          <ActivityFilter
            value={props.activityFilter}
            onSelect={f => props.onActivityFilterChanged(props.blotterTypeFilter, f)}
          />
          {isProductsOrTradesProps(props) && (
            <>
              <ProductFilterComponent
                value={props.productFilter}
                onSelect={f => props.onProductFilterChanged(props.blotterTypeFilter, f)}
              />
              {props.isInternal && (
                <PlatformFilterSelector
                  className="width-fit-content ms-2"
                  platformFilter={props.platformFilter}
                  onPlatformFilterChanged={f => props.onPlatformFilterChanged(props.blotterTypeFilter, f)}
                  fetchingPlatformsStatus={fetchingPlatformsStatus}
                  platforms={platforms}
                />
              )}
            </>
          )}
          <DateFilter value={props.dateFilter} onChange={f => props.onDateFilterChanged(props.blotterTypeFilter, f)} />
          <FullTextSearch
            value={props.quickSearchTerm}
            onChange={f => props.onFullTextSearchChanged(props.blotterTypeFilter, f)}
            debounce
          >
            <AdvancedSearchInfoLink className="btn-flat-secondary btn-xl border-start border-opacity-40 d-inline-block" />
            <ExportCsv
              className="d-inline-block"
              fetch={props.blotterTypeFilter === 'dlpProducts' ? dlpFetch : fetch}
              disabled={props.error}
            />
            <AdvancedFilterButton
              value={props.advancedFilters}
              onToggle={() => props.onAdvancedFilterToggled(props.blotterTypeFilter)}
            />
          </FullTextSearch>
        </div>
        {isProductsOrTradesProps(props) && (
          <div className="w-100 d-flex flex-row">
            {props.advancedFilters && (
              <AdvancedFilters
                currencyFilter={props.currencyFilter}
                onCurrencyFilterChanged={f => props.onCurrencyFilterChanged(props.blotterTypeFilter, f)}
                startDate={props.startDate}
                onStartDateFilterChanged={f => props.onStartDateFilterChanged(props.blotterTypeFilter, f)}
                endDate={props.endDate}
                onEndDateFilterChanged={f => props.onEndDateFilterChanged(props.blotterTypeFilter, f)}
              />
            )}
          </div>
        )}
        {!isProductsOrTradesProps(props) && (
          <div className="w-100 d-flex flex-row justify-content-end border-top">
            {props.advancedFilters && (
              <AdvancedDateFilter
                startDate={props.startDate}
                onStartDateFilterChanged={f => props.onStartDateFilterChanged(props.blotterTypeFilter, f)}
                endDate={props.endDate}
                onEndDateFilterChanged={f => props.onEndDateFilterChanged(props.blotterTypeFilter, f)}
              />
            )}
          </div>
        )}
      </div>
    </GenericErrorBoundary>
  );
};

export const ConnectedTradesBlotterFilter = connectFilterComponent(
  TradesBlotterFilter as any,
) as unknown as ComponentType<FilterOwnProps>;
